<template>
  <div
    class="relative w-full flex flex-col items-center text-white bg-[#0f0f0f] mt-[40px] pt-[30px] lg:pt-[50px] pb-[80px]"
  >
    <!-- Scrollable Content Wrapper -->
    <div class="w-full h-full flex flex-col items-center justify-start">
      <!-- Hero Section -->
      <section
        class="relative z-10 w-full max-w-[1300px] mx-auto px-4 py-8 lg:py-10"
      >
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-stretch min-h-[400px]">
          
          <!-- Left Column -->
          <div class="relative lg:col-span-8 bg-gradient-to-r from-[#460062] via-[#2a0052] to-[#120024] rounded-[24px] p-6 lg:p-10 flex flex-col justify-center text-left shadow-[0_0_40px_rgba(0,0,0,0.5)] overflow-hidden">
            
            <!-- Content Wrapper -->
            <div class="relative z-10 w-full max-w-[500px] flex flex-col items-start">
              <!-- Badge -->
              <div v-if="isLive" class="flex items-center bg-[#ff2a2a] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-widest mb-4">
                <span class="w-1.5 h-1.5 rounded-full bg-white mr-2"></span>
                {{ $t('MainPage.LiveOnKick') }}
              </div>

              <!-- Heading -->
              <h1 class="font-sans font-black uppercase text-5xl sm:text-6xl lg:text-[58px] leading-[0.95] mb-4 tracking-normal">
                <span class="block text-white mb-1">{{ $t('MainPage.PrepareTo') }}</span>
                <span class="block text-[#00F0FF]">{{ $t('MainPage.PlayGreat') }}</span>
              </h1>

              <!-- Paragraph -->
              <p class="font-sans text-gray-300 text-sm lg:text-[16px] mb-6 leading-[1.6] max-w-[400px] font-normal" v-html="$t('MainPage.HeroDescription')"></p>

              <!-- Buttons -->
              <div class="flex flex-wrap gap-4 w-full sm:w-auto">
                <a :href="`https://kick.com/${streamer}`" target="_blank" class="flex-1 sm:flex-none flex items-center justify-center text-center bg-[#00F0FF] text-black font-extrabold text-[14px] px-8 py-3 rounded-xl hover:bg-[#00d4e0] transition-all shadow-[0_0_20px_rgba(0,240,255,0.4)]">
                  {{ $t('MainPage.WatchLive') }}
                </a>
                <a href="https://discord.com/invite/N764VWuGyn" target="_blank" class="flex-1 sm:flex-none flex items-center justify-center text-center bg-[#BC13FE]/50 text-white font-extrabold text-[14px] px-8 py-3 rounded-xl border-2 border-[#BC13FE] shadow-[0_0_20px_rgba(188,19,254,0.5)] hover:bg-[#BC13FE]/80 transition-all">
                  {{ $t('MainPage.JoinDiscord') }}
                </a>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="lg:col-span-4 flex flex-col gap-4 lg:gap-6 h-full">
            
            <!-- Card 1: Guess the Balance -->
            <div class="relative flex-1 bg-[#1a0a2e] rounded-[24px] p-6 flex flex-col justify-between overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)] border border-purple-900/40">
              <!-- Background Image -->
              <img
                src="@/assets/revamp/hero-banner-2.png"
                alt="Guess the Balance"
                class="absolute right-0 top-0 h-full w-[70%] object-cover object-right z-0 pointer-events-none"
                style="-webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%); mask-image: linear-gradient(to right, transparent 0%, black 50%);"
              />
              
              <!-- Content -->
              <div class="relative z-10 flex flex-col items-start h-full justify-center pt-2">
                <h2 class="font-sans font-black text-white text-2xl sm:text-[26px] mb-2 tracking-wide leading-none max-w-[180px]" v-html="$t('MainPage.GuessTheBalance')"></h2>
                <p class="font-sans font-medium text-gray-300 text-xs sm:text-sm leading-relaxed max-w-[180px]">
                  {{ $t('MainPage.GuessDescription') }}
                </p>
              </div>

              <!-- Button -->
              <div class="relative z-10 self-end mt-2">
                <router-link to="/guess-the-balance" class="inline-block text-center bg-[#BC13FE]/50 text-white font-extrabold text-[12px] px-6 py-2 rounded-xl border-2 border-[#BC13FE] shadow-[0_0_15px_rgba(188,19,254,0.5)] hover:bg-[#BC13FE]/80 transition-all">
                  {{ $t('MainPage.SeeResults') }}
                </router-link>
              </div>
            </div>

            <!-- Card 2: Leaderboard -->
            <div class="relative flex-1 bg-[#150a24] rounded-[24px] p-6 flex flex-col justify-between overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)] border border-purple-900/40">
              <!-- Background Image -->
              <img
                src="@/assets/revamp/hero-banner-3.png"
                alt="Leaderboard"
                class="absolute right-0 top-0 h-full w-[75%] object-cover object-right z-0 pointer-events-none"
                style="-webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%); mask-image: linear-gradient(to right, transparent 0%, black 50%);"
              />
              
              <!-- Content -->
              <div class="relative z-10 flex flex-col items-start h-full" :class="{ 'justify-center pt-2': !leaderboardPrize }">
                <h2 class="font-sans font-bold text-white mb-1 tracking-wide" :class="!leaderboardPrize ? 'text-2xl sm:text-[26px] font-black leading-none max-w-[180px]' : 'text-lg sm:text-[20px]'">
                  {{ $t('MainPage.Leaderboard') }}
                </h2>
                <template v-if="leaderboardPrize">
                  <div class="font-sans font-black text-[#cfff04] text-4xl sm:text-[44px] leading-none mb-1 tracking-tight">
                    {{ leaderboardPrize }}
                  </div>
                  <div class="font-sans font-bold text-[#00F0FF] text-[10px] uppercase tracking-widest mb-2">
                    {{ $t('MainPage.PrizePool') }}
                  </div>
                </template>
                <template v-else>
                  <p class="font-sans font-medium text-gray-300 text-xs sm:text-sm leading-relaxed max-w-[180px] mt-1">
                    {{ $t('MainPage.LeaderboardDescription') }}
                  </p>
                </template>
              </div>

              <!-- Button -->
              <div class="relative z-10 self-end mt-2">
                <router-link to="/leaderboards" class="inline-block text-center bg-[#BC13FE]/50 text-white font-extrabold text-[12px] px-6 py-2 rounded-xl border-2 border-[#BC13FE] shadow-[0_0_15px_rgba(188,19,254,0.5)] hover:bg-[#BC13FE]/80 transition-all">
                  {{ $t('MainPage.ViewRankings') }}
                </router-link>
              </div>
            </div>

          </div>
          
        </div>
      </section>

      <!-- Socials Section -->
      <section
        class="relative z-10 flex flex-col items-start text-white px-4 w-full max-w-[1300px] mx-auto mt-8 lg:mt-10 mb-10"
      >
        <h2 class="mb-6 lg:mb-8 text-2xl md:text-[28px] font-extrabold tracking-widest uppercase font-sans text-left w-full">
          {{ $t('MainPage.FollowSocials') }}
        </h2>
        
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 w-full">
          
          <!-- YouTube -->
          <a href="https://www.youtube.com/mahercogaming1" target="_blank" class="flex flex-col items-center justify-center bg-[#1c1c1f] hover:bg-[#25252a] rounded-xl py-6 lg:py-8 w-full transition-all border border-white/5 shadow-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 mb-3"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            <span class="text-[13px] font-bold tracking-wide">Youtube</span>
          </a>

          <!-- Kick -->
          <a href="https://kick.com/maherco" target="_blank" class="flex flex-col items-center justify-center bg-[#1c1c1f] hover:bg-[#25252a] rounded-xl py-6 lg:py-8 w-full transition-all border border-white/5 shadow-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 mb-3">
              <rect x="4" y="2" width="4" height="20" />
              <rect x="8" y="10" width="4" height="4" />
              <rect x="12" y="6" width="4" height="4" />
              <rect x="16" y="2" width="4" height="4" />
              <rect x="12" y="14" width="4" height="4" />
              <rect x="16" y="18" width="4" height="4" />
            </svg>
            <span class="text-[13px] font-bold tracking-wide">Kick</span>
          </a>

          <!-- Discord -->
          <a href="https://discord.com/invite/N764VWuGyn" target="_blank" class="flex flex-col items-center justify-center bg-[#1c1c1f] hover:bg-[#25252a] rounded-xl py-6 lg:py-8 w-full transition-all border border-white/5 shadow-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 mb-3"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg>
            <span class="text-[13px] font-bold tracking-wide">Discord</span>
          </a>

          <!-- Facebook -->
          <a href="https://www.facebook.com/Mahercogaming" target="_blank" class="flex flex-col items-center justify-center bg-[#1c1c1f] hover:bg-[#25252a] rounded-xl py-6 lg:py-8 w-full transition-all border border-white/5 shadow-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 mb-3"><path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z"/></svg>
            <span class="text-[13px] font-bold tracking-wide">Facebook</span>
          </a>

          <!-- Tiktok -->
          <a href="https://www.tiktok.com/@maherco_me" target="_blank" class="flex flex-col items-center justify-center bg-[#1c1c1f] hover:bg-[#25252a] rounded-xl py-6 lg:py-8 w-full transition-all border border-white/5 shadow-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 mb-3"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 15.68a6.34 6.34 0 0010.86 4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.04-.54z"/></svg>
            <span class="text-[13px] font-bold tracking-wide">Tiktok</span>
          </a>



        </div>
      </section>

      <!-- Live Stream Section -->
      <section id="promotions" class="relative z-10 flex flex-col items-start text-white px-4 w-full max-w-[1300px] mx-auto mb-16">
        
        <!-- Header -->
        <div class="flex items-center justify-start w-full mb-6 lg:mb-8 gap-4">
          <h2 class="text-2xl md:text-[28px] font-extrabold tracking-widest uppercase font-sans m-0 leading-none">
            {{ $t('MainPage.LiveStream') }}
          </h2>
          <!-- Red Pill Badge -->
          <div v-if="isLive" class="flex items-center bg-[#ff2a2a] text-white text-[10px] sm:text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest leading-none">
            <span class="w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse"></span>
            {{ $t('MainPage.Live') }}
          </div>
        </div>

        <!-- Full Width Image / Player Container -->
        <div class="relative w-full h-[300px] sm:h-[400px] md:h-[480px] rounded-[24px] overflow-hidden border border-white/10 shadow-2xl bg-[#0a0a0c]">
          
          <!-- Embedded Player -->
          <template v-if="isLive">
            <iframe
              :src="streamUrl"
              frameborder="0"
              scrolling="no"
              allowfullscreen
              class="w-full h-full"
            ></iframe>
          </template>

          <!-- Fallback Image when Offline -->
          <template v-else>
            <!-- Background Image -->
            <img src="@/assets/revamp/hero-banner-4.png" alt="Live Stream Banner" class="absolute inset-0 w-full h-full object-cover object-center transform scale-[1.15] z-0 pointer-events-none" />
            
            <!-- Centered Dark Overlay Box -->
            <div class="relative z-10 w-full h-full flex flex-col items-center justify-center">
              <div class="flex flex-col items-center bg-[#0a0a0c]/80 backdrop-blur-md border border-white/5 px-8 sm:px-12 py-8 rounded-[20px] shadow-2xl">
                <!-- Label -->
                <div class="text-[#00F0FF] font-bold text-[11px] uppercase tracking-[0.2em] mb-4">
                  {{ $t('MainPage.WatchOnKick') }}
                </div>
                <!-- Button -->
                <a :href="`https://kick.com/${streamer}`" target="_blank" class="px-10 py-3.5 text-white font-extrabold text-[15px] bg-black/60 hover:bg-white hover:text-black border border-white/10 rounded-xl transition-all duration-300">
                  {{ $t('MainPage.WatchStream') }}
                </a>
              </div>
            </div>
          </template>
        </div>

      </section>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      isLive: false,
      streamUrl: "https://player.kick.com/maherco?autoplay=true&muted=true",
      streamer: "maherco",
      socialIcons: [
        // { name: "twitter", link: "/" },
        { name: "tiktok", link: "https://www.tiktok.com/@maherco_me" },
        { name: "kick", link: "https://kick.com/maherco" },
        { name: "discord", link: "https://discord.com/invite/N764VWuGyn" },
        { name: "youtube", link: "https://www.youtube.com/mahercogaming1" },
        { name: "instagram", link: "https://www.instagram.com/mahercogaming" },
        { name: "facebook", link: "https://www.facebook.com/Mahercogaming" },
      ],
    };
  },
  computed: {
    leaderboardPrize() {
      return process.env.VUE_APP_PRIZE_POOL || null;
    }
  },
  methods: {
    openPromo(url) {
      window.open(url, "_blank");
    },
    async checkStreamStatus() {
      try {
        const response = await fetch(`https://kick.com/api/v2/channels/${this.streamer}`);
        const data = await response.json();
        this.isLive = data?.livestream?.is_live ?? false;

        if (this.isLive) {
          this.streamUrl = `https://player.kick.com/${this.streamer}?autoplay=true&muted=true`;
        }
      } catch (error) {
        console.error("Error checking stream status:", error);
      }
    },
  },
  mounted() {
    this.checkStreamStatus();
  },
});
</script>
